import React, { useMemo } from 'react';

/* ─────────────────────────────────────────────────────────────
   A QR-shaped picture of a guest code.

   It is not a scannable QR code (that needs Reed-Solomon coding, and
   the demo has no reader to scan it with). It has a real code's finder
   squares, timing rows and alignment mark, and its data modules are
   drawn from a generator seeded by the PIN, so the same code always
   draws the same picture and two codes never look alike. The screen
   labels it as a demo.
   ───────────────────────────────────────────────────────────── */

const N = 25;
const QUIET = 2;

function seeded(seed) {
    let t = seed >>> 0;
    return () => {
        t = (t + 0x6d2b79f5) >>> 0;
        let x = Math.imul(t ^ (t >>> 15), t | 1);
        x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
        return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
    };
}

/* True where the fixed patterns live, so data is not drawn over them. */
function reserved(x, y) {
    const inFinder = (fx, fy) => x >= fx - 1 && x <= fx + 7 && y >= fy - 1 && y <= fy + 7;
    if (inFinder(0, 0) || inFinder(N - 7, 0) || inFinder(0, N - 7)) return true;
    if (x === 6 || y === 6) return true;
    if (x >= 16 && x <= 20 && y >= 16 && y <= 20) return true;
    return false;
}

function modules(value) {
    const seed = [...String(value)].reduce((h, ch) => Math.imul(h ^ ch.charCodeAt(0), 16777619), 2166136261);
    const rand = seeded(seed);
    let d = '';

    for (let y = 0; y < N; y++) {
        for (let x = 0; x < N; x++) {
            let dark;
            if (x === 6 || y === 6) {
                /* Timing rows between the finders alternate. */
                dark = (x === 6 && y > 7 && y < N - 8 && y % 2 === 0) || (y === 6 && x > 7 && x < N - 8 && x % 2 === 0);
            } else if (reserved(x, y)) {
                dark = false;
            } else {
                dark = rand() < 0.5;
            }
            if (dark) d += `M${x + QUIET} ${y + QUIET}h1v1h-1z`;
        }
    }
    return d;
}

function Finder({ x, y }) {
    return (
        <g transform={`translate(${x + QUIET} ${y + QUIET})`}>
            <rect x="0.5" y="0.5" width="6" height="6" rx="1.4" fill="none" stroke="#0b0b0e" strokeWidth="1" />
            <rect x="2" y="2" width="3" height="3" rx="0.7" fill="#0b0b0e" />
        </g>
    );
}

export function PseudoQr({ value, size = 168, label = 'Demo QR' }) {
    const d = useMemo(() => modules(value), [value]);
    const box = N + QUIET * 2;

    return (
        <figure className="acc-qr" style={{ width: size }}>
            <svg viewBox={`0 0 ${box} ${box}`} width={size} height={size} role="img" aria-label={`${label || 'QR picture'} for code ${value}`} shapeRendering="crispEdges">
                <rect width={box} height={box} rx="2.2" fill="#f5f5f7" />
                <path d={d} fill="#0b0b0e" />
                <Finder x={0} y={0} />
                <Finder x={N - 7} y={0} />
                <Finder x={0} y={N - 7} />
                <g transform={`translate(${16 + QUIET} ${16 + QUIET})`}>
                    <rect x="0.5" y="0.5" width="4" height="4" rx="0.8" fill="none" stroke="#0b0b0e" strokeWidth="1" />
                    <rect x="2" y="2" width="1" height="1" fill="#0b0b0e" />
                </g>
            </svg>
            {label && <figcaption className="acc-qr__label">{label}</figcaption>}
        </figure>
    );
}
